import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play, Youtube, Film, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { CHURCH } from '@/api/client';
import Seo from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import SectionTitle from '@/components/SectionTitle';

function ytThumb(videoId) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
}

export default function Media() {
  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['public-media'],
    queryFn: async () => (await api.get('/public/media')).data,
  });
  const [activeId, setActiveId] = useState(null);

  const active = useMemo(() => videos.find((v) => v.id === activeId) || videos[0], [videos, activeId]);

  return (
    <div>
      <Seo
        title="Media Gallery — Ministry of Jesus, Dublin"
        titleExact
        description="Watch the story of Ministry of Jesus in Dublin — Sunday worship, prayer nights, evangelism, VBS, community events and highlights from our church history."
        path="/media"
        keywords={['Ministry of Jesus videos', 'Pentecostal church Dublin YouTube', 'church history videos Ireland']}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Media', path: '/media' }])]}
      />

      {/* HERO */}
      <section className="gradient-purple text-white py-10 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Film size={14}/> Church Media Gallery
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4 leading-tight">Our Story in Video</h1>
          <p className="text-lg text-white/90 max-w-3xl">
            Moments from the life of Ministry of Jesus — Sunday worship, prayer nights, evangelism, children’s ministry,
            baptisms and highlights from our journey as a Pentecostal church in Ireland.
          </p>
          <a
            href={CHURCH.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold mt-6 inline-flex"
            data-testid="media-hero-yt"
          >
            <Youtube size={16}/> Subscribe on YouTube
          </a>
        </div>
      </section>

      {/* FEATURED + GRID */}
      <section className="py-14 bg-moj-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {isLoading && (
            <div className="text-center text-moj-charcoal/60 py-16">Loading videos…</div>
          )}

          {!isLoading && videos.length === 0 && (
            <div className="card-moj p-10 sm:p-14 text-center max-w-2xl mx-auto" data-testid="media-empty">
              <div className="w-16 h-16 rounded-full gradient-purple flex items-center justify-center mx-auto mb-5">
                <Film size={28} className="text-moj-gold"/>
              </div>
              <h2 className="font-serif text-2xl text-moj-purple-deep mb-3">Videos coming soon</h2>
              <p className="text-moj-charcoal/80 mb-6">
                We’re preparing highlights from our worship services and events. In the meantime, subscribe to our YouTube channel to be notified.
              </p>
              <a href={CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="btn-primary">
                <Youtube size={16}/> Subscribe on YouTube <ArrowRight size={14}/>
              </a>
            </div>
          )}

          {!isLoading && videos.length > 0 && (
            <>
              {/* Featured player */}
              <article className="card-moj overflow-hidden mb-10" data-testid="media-featured">
                <div className="relative aspect-video bg-moj-purple-deep">
                  {active?.video_id && (
                    <iframe
                      key={active.video_id}
                      title={active.title}
                      src={`https://www.youtube.com/embed/${active.video_id}?rel=0&modestbranding=1`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
                <div className="p-5 sm:p-6">
                  <h2 className="font-serif text-2xl text-moj-purple-deep leading-tight mb-2">{active?.title}</h2>
                  {active?.description && <p className="text-moj-charcoal/85 whitespace-pre-line">{active.description}</p>}
                </div>
              </article>

              <SectionTitle kicker="Playlist" title="More from our channel" subtitle="Tap any tile to play in the featured player above."/>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-8">
                {videos.map((v) => {
                  const isActive = active?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setActiveId(v.id)}
                      className={`card-moj text-left overflow-hidden flex flex-col transition group ${isActive ? 'ring-2 ring-moj-gold shadow-lg' : 'hover:shadow-md'}`}
                      data-testid={`media-item-${v.id}`}
                    >
                      <div className="relative aspect-video bg-moj-purple-deep">
                        <img
                          src={ytThumb(v.video_id)}
                          alt={v.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className={`absolute inset-0 bg-black/30 flex items-center justify-center transition ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <div className="w-14 h-14 rounded-full bg-moj-gold flex items-center justify-center shadow-xl">
                            <Play size={22} fill="#2B0038" className="text-moj-purple-deep ml-1"/>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 flex-1">
                        <h3 className="font-serif text-lg text-moj-purple-deep leading-tight line-clamp-2">{v.title}</h3>
                        {v.description && (
                          <p className="text-xs text-moj-charcoal/70 mt-2 line-clamp-2">{v.description}</p>
                        )}
                        <div className="flex items-center gap-1 text-[11px] text-moj-charcoal/50 mt-3">
                          <Clock size={11}/> Added recently
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* SUBSCRIBE CTA */}
      <section className="py-14 gradient-purple text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Youtube size={40} className="text-moj-gold mx-auto mb-3"/>
          <h2 className="font-serif text-3xl text-white mb-3">Never miss a service</h2>
          <p className="text-white/90 mb-5">
            Subscribe to <strong>@MinistryofJesus</strong> on YouTube for Sunday worship, Daily Devotion, testimonies and prayer nights.
          </p>
          <a href={CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="btn-gold">
            <Youtube size={18}/> Subscribe on YouTube <ArrowRight size={16}/>
          </a>
          <p className="text-xs text-white/60 mt-8">
            <Link to="/live" className="underline hover:text-moj-gold">Watch our Sunday Service LIVE</Link>
            {' · '}
            <Link to="/morning-prayer" className="underline hover:text-moj-gold">Join Early Morning Prayer</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
